import type { TFunction } from 'i18next';
import type {
  RtiCustomFieldToggle,
  RtiEmployeeDataFields,
  RtiFieldLeaf,
  RtiFieldSection,
  RtiEmployeeDataSectionKey,
} from 'model/interfaces/rti-employee-data-fields';

export const RTI_FIELD_TRANSLATION_KEYS: Record<string, string> = {
  employee_name: 'pe.settings.rti.field.employeeName',
  dept_function: 'pe.settings.rti.field.deptFunction',
  job_level_grade: 'pe.settings.rti.field.jobLevelGrade',
  subfunction: 'pe.settings.rti.field.subfunction',
  job_code: 'pe.db.factor.JobCode',
  job_group: 'pe.costPlanning.individualPayEquity.jobGroupLabel',
  city: 'pe.settings.rti.field.city',
  state_province: 'pe.settings.rti.field.stateProvince',
  perf_rating_most_recent: 'pe.settings.rti.field.perfRatingMostRecent',
  perf_rating_prior_year: 'pe.settings.rti.field.perfRatingPriorYear',
  perf_rating_two_years_ago: 'pe.settings.rti.field.perfRatingTwoYearsAgo',
  base_salary: 'pe.db.comp.Base Salary',
  total_cash: 'pe.db.comp.Total cash',
  total_direct_comp: 'pe.db.comp.Total Direct Comp',
  twelve_month_total_lti_paid: 'pe.settings.rti.field.twelveMonthTotalLtiPaid',
  twelve_month_total_sti_bonus_paid: 'pe.settings.rti.field.twelveMonthTotalStiBonusPaid',
  company: 'pe.costPlanning.individualPayEquity.companyLabel',
  function_grade: 'pe.settings.rti.comparisonGroups.functionGrade',
  subfunction_grade: 'pe.settings.rti.comparisonGroups.subfunctionGrade',
};

export const RTI_FIELD_HINT_KEYS: Record<string, string> = {
  total_cash: 'pe.settings.rti.fieldHint.totalCash',
  total_direct_comp: 'pe.settings.rti.fieldHint.totalDirectComp',
};

export function getRtiFieldLabel(fieldKey: string, t: TFunction): string {
  const customFieldMatch = /^custom_field_(\d+)$/.exec(fieldKey);
  if (customFieldMatch) {
    return t('pe.settings.rti.field.customField', { number: customFieldMatch[1] });
  }
  const customCompMatch = /^custom_comp_field_(\d+)$/.exec(fieldKey);
  if (customCompMatch) {
    return t('pe.settings.rti.field.customCompField', { number: customCompMatch[1] });
  }
  const explicitKey = RTI_FIELD_TRANSLATION_KEYS[fieldKey];
  if (explicitKey) {
    return t(explicitKey, { defaultValue: humanizeRtiFieldKey(fieldKey) });
  }
  return humanizeRtiFieldKey(fieldKey);
}

export const RTI_TWELVE_MONTH_COMPENSATION_FIELD_KEYS = [
  'twelve_month_total_lti_paid',
  'twelve_month_total_sti_bonus_paid',
] as const;

export function isRtiTwelveMonthCompensationFieldKey(fieldKey: string): boolean {
  return (RTI_TWELVE_MONTH_COMPENSATION_FIELD_KEYS as readonly string[]).includes(fieldKey);
}

const SECTION_KEYS: RtiEmployeeDataSectionKey[] = [
  'employee_information',
  'job_information',
  'location',
  'compensation',
];

const DEFAULT_TRUE_BOOLEAN_KEYS: Record<RtiEmployeeDataSectionKey, readonly string[]> = {
  employee_information: ['employee_name'],
  job_information: ['job_level_grade', 'dept_function', 'subfunction', 'job_code'],
  location: [],
  compensation: ['base_salary', 'total_cash', 'total_direct_comp'],
};

function mergeDefaultBooleanKeys(sectionKey: RtiEmployeeDataSectionKey, section: RtiFieldSection): RtiFieldSection {
  const keys = DEFAULT_TRUE_BOOLEAN_KEYS[sectionKey];
  if (!keys.length) return section;
  const out: RtiFieldSection = { ...section };
  for (const k of keys) {
    if (!(k in out)) {
      out[k] = true;
    }
  }
  return out;
}

export function isCustomFieldValue(value: unknown): value is RtiCustomFieldToggle {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Record<string, unknown>;
  return 'enabled' in o || 'label' in o;
}

export function humanizeRtiFieldKey(fieldKey: string): string {
  return fieldKey
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function rtiCustomCompFieldTemplateDisplayLabel(fieldKey: string): string | undefined {
  const m = /^custom_comp_field_(\d+)$/.exec(fieldKey);
  if (!m) return undefined;
  return `Custom Compensation Field ${m[1]}`;
}

export function rtiCustomToggleStaticCheckboxLabel(fieldKey: string): string {
  return rtiCustomCompFieldTemplateDisplayLabel(fieldKey) ?? humanizeRtiFieldKey(fieldKey);
}

function isCustomCompensationFieldKey(fieldKey: string): boolean {
  return /^custom_comp_field_\d+$/.test(fieldKey);
}

function isRtiEmployeeInformationCustomFieldKey(fieldKey: string): boolean {
  return /^custom_field_\d+$/.test(fieldKey);
}

export function tryCoerceLooseRtiObjectLeaf(fieldKey: string, raw: unknown): RtiCustomFieldToggle | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  if (isCustomFieldValue(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  let enabled: boolean;
  if (typeof o.enabled === 'boolean') {
    enabled = o.enabled;
  } else if (typeof o.selected === 'boolean') {
    enabled = o.selected;
  } else if (typeof o.checked === 'boolean') {
    enabled = o.checked;
  } else {
    const l = typeof o.label === 'string' ? o.label : '';
    return { label: l, enabled: false };
  }
  const rawLabel = typeof o.label === 'string' ? o.label : '';
  const label =
    rawLabel.length > 0
      ? rawLabel
      : isCustomCompensationFieldKey(fieldKey) ||
          isRtiEmployeeInformationCustomFieldKey(fieldKey) ||
          isRtiTwelveMonthCompensationFieldKey(fieldKey)
        ? ''
        : humanizeRtiFieldKey(fieldKey);
  return { label, enabled };
}

function normalizeFieldValue(fieldKey: string, raw: unknown): RtiFieldLeaf | null {
  if (typeof raw === 'boolean') return raw;
  if (raw === null) {
    if (isCustomCompensationFieldKey(fieldKey) || isRtiTwelveMonthCompensationFieldKey(fieldKey)) {
      return { label: '', enabled: false };
    }
    if (isRtiEmployeeInformationCustomFieldKey(fieldKey)) {
      return { label: '', enabled: false };
    }
    return false;
  }
  if (isCustomFieldValue(raw)) {
    const o = raw as Record<string, unknown>;
    const rawLabel = typeof o.label === 'string' ? o.label : '';
    const label =
      rawLabel.length > 0
        ? rawLabel
        : isCustomCompensationFieldKey(fieldKey)
          ? ''
          : humanizeRtiFieldKey(fieldKey);
    const enabled = typeof o.enabled === 'boolean' ? o.enabled : false;
    return { label, enabled };
  }
  const loose = tryCoerceLooseRtiObjectLeaf(fieldKey, raw);
  if (loose) {
    return loose;
  }
  return null;
}

function normalizeSection(raw: unknown): RtiFieldSection {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: RtiFieldSection = {};
  for (const [key, val] of Object.entries(raw)) {
    const normalized = normalizeFieldValue(key, val);
    if (normalized !== null) {
      out[key] = normalized;
    }
  }
  return out;
}

function migrateEmployeeInformationSection(section: RtiFieldSection): RtiFieldSection {
  const legacyFirst = section['first_name'];
  const legacyLast = section['last_name'];
  const out: RtiFieldSection = { ...section };
  delete out['first_name'];
  delete out['last_name'];
  const legacyEnabled = legacyFirst === true || legacyLast === true;
  if (legacyEnabled && !('employee_name' in out)) {
    out['employee_name'] = true;
  }
  return out;
}

const LOCATION_CANONICAL_BOOLEANS = ['city', 'state_province'] as const;
const JOB_INFORMATION_CANONICAL_BOOLEANS = [
  'job_code',
  'job_group',
  'subfunction',
  'dept_function',
  'job_level_grade',
] as const;
const PERF_RATING_BOOLEANS = [
  'perf_rating_prior_year',
  'perf_rating_most_recent',
  'perf_rating_two_years_ago',
] as const;
export const EMPLOYEE_INFO_CUSTOM_FIELD_KEYS = [
  'custom_field_1',
  'custom_field_2',
  'custom_field_3',
  'custom_field_4',
  'custom_field_5',
] as const;
const COMPENSATION_CUSTOM_COMP_KEYS = [
  'custom_comp_field_1',
  'custom_comp_field_2',
  'custom_comp_field_3',
  'custom_comp_field_4',
  'custom_comp_field_5',
] as const;

function ensureCanonicalBooleanLeaves(
  sectionKey: RtiEmployeeDataSectionKey,
  section: RtiFieldSection,
): RtiFieldSection {
  const out: RtiFieldSection = { ...section };
  const fillBools = (keys: readonly string[]) => {
    for (const k of keys) {
      if (!(k in out)) {
        out[k] = false;
      }
    }
  };
  if (sectionKey === 'location') {
    fillBools(LOCATION_CANONICAL_BOOLEANS);
  } else if (sectionKey === 'job_information') {
    fillBools(JOB_INFORMATION_CANONICAL_BOOLEANS);
  } else if (sectionKey === 'employee_information') {
    fillBools(PERF_RATING_BOOLEANS);
    for (const k of EMPLOYEE_INFO_CUSTOM_FIELD_KEYS) {
      if (!(k in out)) {
        out[k] = { label: '', enabled: false };
      }
    }
  } else if (sectionKey === 'compensation') {
    for (const k of COMPENSATION_CUSTOM_COMP_KEYS) {
      if (!(k in out)) {
        out[k] = { label: '', enabled: false };
      }
    }
    for (const k of RTI_TWELVE_MONTH_COMPENSATION_FIELD_KEYS) {
      if (!(k in out)) {
        out[k] = { label: '', enabled: false };
      }
    }
  }
  return out;
}

export function defaultRtiEmployeeDataFields(): RtiEmployeeDataFields {
  return {
    employee_information: {},
    job_information: {},
    location: {},
    compensation: {},
  };
}

export function normalizeRtiEmployeeDataFields(raw: unknown): RtiEmployeeDataFields {
  const base = defaultRtiEmployeeDataFields();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    const empty: RtiEmployeeDataFields = { ...base };
    for (const section of SECTION_KEYS) {
      empty[section] = ensureCanonicalBooleanLeaves(section, mergeDefaultBooleanKeys(section, {}));
    }
    return empty;
  }
  const d = raw as Record<string, unknown>;
  const result: RtiEmployeeDataFields = { ...base };
  for (const section of SECTION_KEYS) {
    const normalizedRaw =
      section in d && d[section] !== undefined ? normalizeSection(d[section]) : {};
    const normalized =
      section === 'employee_information'
        ? migrateEmployeeInformationSection(normalizedRaw)
        : normalizedRaw;
    result[section] = ensureCanonicalBooleanLeaves(
      section,
      mergeDefaultBooleanKeys(section, normalized),
    );
  }
  return result;
}
